import React, { useState, useEffect, useMemo } from "react";
import { TL } from "../i18n";
import { Card, Badge, Button } from "./ui";
import { Cake, Heart, Flower2, CalendarDays, ChevronDown, ChevronUp, Bell, Check, Eraser, X, RotateCcw } from "lucide-react";

// NEAREST — Notable Event Alerts and Reactions for Education, Sentiment and Thanksgiving.
// Rolling 3-month calendar (from today). ONLY checked milestones are highlighted; the year
// count (N) is shown on each highlighted day. Flasher lists enabled milestones within 35 days.
export const MILESTONES = [1, 3, 5, 7, 12, 13, 15, 18, 20, 21, 25, 40, 50, 60, 70, 80, 90, 100];
const TYPES = [
  { key: "birthday", label: "Birthday", color: "#0F62FE", Icon: Cake, verb: "birthday" },
  { key: "wedding", label: "Wedding", color: "#24A148", Icon: Heart, verb: "anniversary" },
  { key: "death", label: "Memorial", color: "#DA1E28", Icon: Flower2, verb: "memorial" },
];
const T_BY = Object.fromEntries(TYPES.map((t) => [t.key, t]));
const EMPTY = { birthday: [], wedding: [], death: [] };
const parseDate = (str) => {
  const m = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(String(str || ""));
  if (!m) return null;
  return { y: +m[1], mo: +m[2], d: +m[3] };
};
const ord = (n) => { const s = ["th", "st", "nd", "rd"], v = n % 100; return n + (s[(v - 20) % 10] || s[v] || s[0]); };
const fmt = (dt) => dt.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
const key = (y, m, d) => `${y}-${m}-${d}`;
const loadApplied = () => { try { return { ...EMPTY, ...(JSON.parse(localStorage.getItem("nearest_milestones")) || {}) }; } catch { return { ...EMPTY }; } };

export default function NearestPanel({ people = [], nameOf = () => "Unnamed" }) {
  const [open, setOpen] = useState(true);
  const [applied, setApplied] = useState(loadApplied);   // committed selection used for computation
  const [draft, setDraft] = useState(applied);           // checkbox selection, applied on click
  const dirty = JSON.stringify(draft) !== JSON.stringify(applied);
  const hasAny = ["birthday", "wedding", "death"].some((k) => (applied[k] || []).length);

  const toggle = (type, n) => setDraft((s) => {
    const arr = s[type] || [];
    return { ...s, [type]: arr.includes(n) ? arr.filter((x) => x !== n) : [...arr, n].sort((a, b) => a - b) };
  });
  const apply = () => { setApplied(draft); localStorage.setItem("nearest_milestones", JSON.stringify(draft)); };
  const clearAll = () => setDraft({ ...EMPTY });

  // Dismissed (acknowledged) alerts — stops the flash for a specific person+event+milestone.
  const [dismissed, setDismissed] = useState(() => { try { return JSON.parse(localStorage.getItem("nearest_dismissed")) || []; } catch { return []; } });
  const dismiss = (id) => setDismissed((s) => { const n = s.includes(id) ? s : [...s, id]; localStorage.setItem("nearest_dismissed", JSON.stringify(n)); return n; });
  const restoreDismissed = () => { setDismissed([]); localStorage.removeItem("nearest_dismissed"); };

  const events = useMemo(() => {
    const ev = [];
    people.forEach((p) => {
      const b = parseDate(p.dob); if (b) ev.push({ type: "birthday", p, ...b });
      const w = parseDate(p.attrs?.fx1); if (w) ev.push({ type: "wedding", p, ...w });
      const dd = parseDate(p.dod); if (dd) ev.push({ type: "death", p, ...dd });
    });
    return ev;
  }, [people]);

  const { dayMap, milestones, flashDays, months, today, anchorYear, dismissedCount } = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    // 15-month span, anchored at Jan of the current year through Mar of next year.
    // The anchor year rolls over on January 5 (before Jan 5 we stay on the prior year's span).
    const jan5 = new Date(today.getFullYear(), 0, 5); jan5.setHours(0, 0, 0, 0);
    const anchorYear = today >= jan5 ? today.getFullYear() : today.getFullYear() - 1;
    const windowStart = new Date(anchorYear, 0, 1); windowStart.setHours(0, 0, 0, 0);
    const windowEnd = new Date(anchorYear + 1, 2, 31); windowEnd.setHours(0, 0, 0, 0);
    const flashEnd = new Date(today); flashEnd.setDate(today.getDate() + 35);
    const dm = {}; const ms = []; const fd = new Set(); let dismissedCount = 0;
    events.forEach((e) => {
      (applied[e.type] || []).forEach((n) => {
        const md = new Date(e.y + n, e.mo - 1, e.d); md.setHours(0, 0, 0, 0);
        if (md < windowStart || md > windowEnd) return;
        const days = Math.round((md - today) / 86400000);
        const k = key(md.getFullYear(), md.getMonth() + 1, md.getDate());
        const id = `${e.type}:${e.p.id}:${n}`;
        (dm[k] = dm[k] || []).push({ type: e.type, n, name: nameOf(e.p) });
        if (md >= today && md <= flashEnd) {
          if (dismissed.includes(id)) { dismissedCount++; }
          else { ms.push({ type: e.type, p: e.p, n, date: md, days, id }); fd.add(k); }
        }
      });
    });
    ms.sort((a, b) => a.date - b.date);
    const months = [];
    let cur = new Date(anchorYear, 0, 1);
    const last = new Date(anchorYear + 1, 2, 1);
    while (cur <= last) { months.push(new Date(cur)); cur.setMonth(cur.getMonth() + 1); }
    return { dayMap: dm, milestones: ms, flashDays: fd, months, today, anchorYear, dismissedCount };
  }, [events, applied, nameOf, dismissed]);

  const renderMonth = (mStart) => {
    const y = mStart.getFullYear(), m = mStart.getMonth();
    const lead = new Date(y, m, 1).getDay();
    const days = new Date(y, m + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < lead; i++) cells.push(null);
    for (let d = 1; d <= days; d++) cells.push(d);
    return (
      <div key={`${y}-${m}`} className="mb-3">
        <div className="mb-1 text-xs font-semibold">{mStart.toLocaleDateString(undefined, { month: "long", year: "numeric" })}</div>
        <div className="grid grid-cols-7 gap-0.5 text-center text-[9px] opacity-50">
          {["S", "M", "T", "W", "T", "F", "S"].map((w, i) => <div key={i}>{w}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-0.5">
          {cells.map((d, i) => {
            if (!d) return <div key={i} />;
            const cd = new Date(y, m, d); cd.setHours(0, 0, 0, 0);
            const past = cd < today;
            const k = key(y, m + 1, d);
            const entries = dayMap[k];
            const flash = flashDays.has(k);
            const isToday = cd.getTime() === today.getTime();
            return (
              <div key={i} data-testid={entries ? `nearest-day-${k}` : undefined}
                title={entries ? entries.map((e) => `${e.name} — ${ord(e.n)} ${T_BY[e.type].verb}`).join("\n") : ""}
                className={`relative flex aspect-square flex-col items-center justify-center rounded text-[10px] ${past && !entries ? "opacity-40" : ""} ${isToday ? "ring-1 ring-[var(--primary)]" : ""} ${flash ? "alert-flash" : ""}`}
                style={{ background: entries ? "rgba(127,127,127,0.12)" : undefined }}>
                <span className="leading-none">{d}</span>
                {entries && (
                  <div className="mt-0.5 flex flex-wrap justify-center gap-x-1 leading-none">
                    {entries.map((en, idx) => (
                      <span key={idx} className="text-[8px] font-extrabold" style={{ color: T_BY[en.type].color }} data-testid={`nearest-yr-${k}-${en.type}-${en.n}`}>{en.n}</span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <aside className="xl:w-[400px] xl:shrink-0" data-testid="nearest-panel">
      <Card className="space-y-3 xl:sticky xl:top-4">
        <div className="flex items-center gap-2">
          <Bell size={16} className={milestones.length ? "alert-flash rounded-full text-[#DA1E28]" : "text-[var(--primary)]"} />
          <h3 className="font-head text-lg">{TL("NEAREST")}</h3>
          {milestones.length > 0 && <Badge color="danger" data-testid="nearest-count">{milestones.length}</Badge>}
          <button className="ms-auto opacity-60 hover:opacity-100" onClick={() => setOpen((o) => !o)} data-testid="nearest-collapse">
            {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </div>
        <p className="text-[11px] opacity-60"><i>{TL("Notable event alerts and reactions for education, sentiment, and thanksgiving")}</i> <b>{TL("(“NEAREST”)")}</b> across a 15-month span (Jan this year → Mar next year, rolling over Jan 5). Only the milestones you check below are highlighted (with their year count); those within the next 35 days flash. Click <b>{TL("Apply")}</b> {TL("after changing boxes.")}</p>

        {open && (
          <>
            {/* 35-day milestone flasher */}
            <div className="space-y-1.5" data-testid="nearest-flasher">
              <div className="text-xs font-semibold opacity-70">{TL("Next 35 days")}</div>
              {!hasAny
                ? <div className="text-xs opacity-60">{TL("No milestones checked. Tick some boxes below and press Apply.")}</div>
                : milestones.length === 0
                  ? <div className="text-xs opacity-60">{TL("No checked milestones fall in the next 35 days.")}</div>
                  : milestones.map((ms, i) => {
                    const Tt = T_BY[ms.type]; const Icon = Tt.Icon;
                    return (
                      <div key={i} data-testid="nearest-milestone" className="alert-flash flex items-center gap-2 rounded-md border p-2" style={{ borderColor: Tt.color }}>
                        <Icon size={16} style={{ color: Tt.color }} />
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-medium">{nameOf(ms.p)}</div>
                          <div className="text-[11px] opacity-70">{ord(ms.n)} {Tt.verb} · {fmt(ms.date)}</div>
                        </div>
                        <Badge>{ms.days === 0 ? "today" : `${ms.days}d`}</Badge>
                        <button onClick={() => dismiss(ms.id)} data-testid={`nearest-dismiss-${ms.id}`} title={TL("Dismiss this alert")} className="opacity-50 hover:opacity-100 hover:text-[#DA1E28]"><X size={15} /></button>
                      </div>
                    );
                  })}
              {dismissedCount > 0 && (
                <button onClick={restoreDismissed} data-testid="nearest-restore-dismissed" className="flex items-center gap-1 text-[11px] text-[var(--primary)] hover:underline">
                  <RotateCcw size={12} /> Show {dismissedCount} dismissed alert{dismissedCount > 1 ? "s" : ""}
                </button>
              )}
            </div>

            {/* Rolling 3-month calendar */}
            <div>
              <div className="mb-1 flex items-center gap-2 text-xs font-semibold opacity-70"><CalendarDays size={13} /> Jan {anchorYear} – Mar {anchorYear + 1} <span className="opacity-50">{TL("(scroll)")}</span></div>
              <div className="max-h-[440px] overflow-y-auto pe-1" data-testid="nearest-calendar">
                {months.map(renderMonth)}
              </div>
              <div className="mt-2 flex flex-wrap gap-3 text-[10px] opacity-70">
                {TYPES.map((t) => <span key={t.key} className="flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ background: t.color }} /> {t.label}</span>)}
              </div>
            </div>

            {/* Milestone toggles — identical option list for each event type */}
            <div className="border-t border-black/10 pt-2 dark:border-white/10">
              <div className="mb-2 flex items-center gap-2">
                <span className="text-xs font-semibold opacity-70">{TL("Milestones to watch (years)")}</span>
                <div className="ms-auto flex items-center gap-2">
                  {dirty && <span className="text-[10px] font-semibold text-[#FF832B]" data-testid="nearest-dirty">{TL("unapplied")}</span>}
                  <button onClick={clearAll} data-testid="nearest-clear" className="flex items-center gap-1 rounded border border-black/15 px-2 py-1 text-[11px] opacity-70 hover:opacity-100 dark:border-white/15"><Eraser size={12} /> {TL("Clear")}</button>
                  <Button onClick={apply} disabled={!dirty} data-testid="nearest-apply" className="!px-2.5 !py-1 !text-[11px]"><Check size={13} /> {TL("Apply")}</Button>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {TYPES.map((Tt) => (
                  <div key={Tt.key}>
                    <div className="mb-1 flex items-center gap-1 text-[11px] font-semibold" style={{ color: Tt.color }}>
                      <Tt.Icon size={12} /> {Tt.label}
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {MILESTONES.map((n) => {
                        const on = (draft[Tt.key] || []).includes(n);
                        return (
                          <button key={n} data-testid={`nearest-toggle-${Tt.key}-${n}`} onClick={() => toggle(Tt.key, n)}
                            className={`rounded px-1.5 py-0.5 text-[10px] transition ${on ? "text-white" : "opacity-70 hover:opacity-100"}`}
                            style={on ? { background: Tt.color } : { border: "1px solid rgba(127,127,127,0.35)" }}>{n}</button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </Card>
    </aside>
  );
}
